import { useCallback } from 'react';
import VerticalViewBase from '../common/VerticalViewBase';
import BookingCardGolf from './BookingCardGolf';
import BookingCardGolfv2 from './BookingCardGolfv2';
import { useAuth } from '../../../../context/authContext';

/**
 * Builds per-slot occupancy entries for a Golf interval booking.
 * Each group of ordered resources that starts at a different offset
 * and each sequential resource switch contributes to its own slot label.
 */
const getSegmentResourceId = seg => {
  const ridRaw = seg?.resourceId?._id || seg?.resourceId || null;
  return ridRaw ? String(ridRaw) : null;
};

const getSegmentResourceName = (seg, orderedResourcesConfig) => {
  if (seg?.resourceId && typeof seg.resourceId === 'object' && seg.resourceId?.name) {
    return String(seg.resourceId.name);
  }
  const rid = getSegmentResourceId(seg);
  if (!rid) return '';
  const nameById = orderedResourcesConfig?.nameById;
  if (nameById instanceof Map) {
    return nameById.get(rid) || rid.substring(0, 6);
  }
  return rid.substring(0, 6);
};

const formatSlotEntry = (startDate, offsetMinutes, timeFormatter) => {
  const slotDate = new Date(startDate.getTime() + offsetMinutes * 60000);
  return {
    label: timeFormatter.format(slotDate),
    sortKey: slotDate.getHours() * 60 + slotDate.getMinutes(),
  };
};

// Adds slot metadata so the timeline can distinguish staggered group starts
// from resource changes within the same group.
const annotateSlotEntries = entries => {
  const sorted = Array.isArray(entries) ? entries.slice().sort((a, b) => a.sortKey - b.sortKey) : [];
  const groupStartEntries = sorted.filter(entry => entry.type !== 'resourceSwitch');
  const totalWaves = groupStartEntries.length;
  const firstStartLabel = groupStartEntries[0]?.label || null;
  const laterWaves = groupStartEntries.slice(1).map(e => ({ label: e.label, participants: e.occupied }));
  let waveIndex = 0;

  return sorted.map(entry => {
    if (entry.type === 'resourceSwitch') {
      return {
        ...entry,
        wave: {
          type: 'resourceSwitch',
          isResourceSwitch: true,
          participants: entry.occupied,
          firstStartLabel,
          resourceSwitch: entry.resourceSwitch || null,
        },
      };
    }

    waveIndex += 1;
    return {
      ...entry,
      wave: {
        type: 'wave',
        waveIndex,
        totalWaves,
        isFirstWave: waveIndex === 1,
        participants: entry.occupied,
        firstStartLabel,
        laterWaves: waveIndex === 1 ? laterWaves : [],
      },
    };
  });
};

const resolveGolfSlotOccupancy = (
  booking,
  startDate,
  hasValidDate,
  timeFormatter,
  orderedResourcesConfig,
  { includeResourceSwitchEntries = true } = {}
) => {
  const segments = Array.isArray(booking?.orderedResourceIds) ? booking.orderedResourceIds : [];
  if (segments.length === 0 || !hasValidDate) {
    // Fallback: all participants at booking start
    const label = timeFormatter.format(startDate);
    const sortKey = startDate.getHours() * 60 + startDate.getMinutes();
    const occupied = Number(booking?.participantCount || 0);
    return annotateSlotEntries([{ label, sortKey, occupied, type: 'groupStart' }]);
  }

  // Collect per-group segment data so group starts and resource switches can
  // produce separate timeline entries.
  const groupMap = new Map();
  segments.forEach((seg, index) => {
    const groupIdxRaw = Number(seg?.groupIndex);
    const groupIdx = Number.isFinite(groupIdxRaw) && groupIdxRaw > 0 ? groupIdxRaw : 1;
    const offsetRaw = Number(seg?.startOffsetMinutes);
    const offset = Number.isFinite(offsetRaw) ? offsetRaw : 0;
    const orderPositionRaw = Number(seg?.orderPosition);
    const orderPosition =
      Number.isFinite(orderPositionRaw) && orderPositionRaw > 0 ? orderPositionRaw : index + 1;
    const participants = Number(seg?.participantCount || 0);

    if (!groupMap.has(groupIdx)) {
      groupMap.set(groupIdx, []);
    }
    groupMap.get(groupIdx).push({
      seg,
      resourceId: getSegmentResourceId(seg),
      offset,
      orderPosition,
      participants,
    });
  });

  const groupEntries = Array.from(groupMap.values()).map(groupSegments => {
    const sortedSegments = groupSegments.slice().sort((a, b) => {
      if (a.offset !== b.offset) {
        return a.offset - b.offset;
      }
      return a.orderPosition - b.orderPosition;
    });
    const minOffset = sortedSegments.length > 0 ? sortedSegments[0].offset : 0;
    const participants =
      sortedSegments.find(seg => Number.isFinite(seg.participants) && seg.participants > 0)
        ?.participants || 0;
    return {
      minOffset,
      participants,
      segments: sortedSegments,
    };
  });

  // If no per-group participant counts were found, distribute total evenly across groups
  const totalFromGroups = groupEntries.reduce((s, g) => s + g.participants, 0);
  if (totalFromGroups <= 0) {
    const total = Number(booking?.participantCount || 0);
    const perGroup = groupEntries.length > 0 ? Math.ceil(total / groupEntries.length) : total;
    for (const g of groupEntries) g.participants = perGroup;
  }

  // Build one entry per unique start time
  const slotMap = new Map();
  for (const { minOffset, participants, segments: groupSegments } of groupEntries) {
    const { label, sortKey } = formatSlotEntry(startDate, minOffset, timeFormatter);
    const existing = slotMap.get(label);
    if (!existing) {
      slotMap.set(label, { label, sortKey, occupied: participants, type: 'groupStart' });
    } else {
      existing.occupied += participants;
    }

    let previousDistinctSegment = null;
    for (const segmentEntry of groupSegments) {
      if (!segmentEntry.resourceId) {
        continue;
      }

      if (!previousDistinctSegment) {
        previousDistinctSegment = segmentEntry;
        continue;
      }

      if (segmentEntry.resourceId === previousDistinctSegment.resourceId) {
        continue;
      }

      if (includeResourceSwitchEntries && segmentEntry.offset > minOffset) {
        const switchSlot = formatSlotEntry(startDate, segmentEntry.offset, timeFormatter);
        const switchKey = `switch:${switchSlot.label}:${previousDistinctSegment.resourceId}:${segmentEntry.resourceId}`;
        const existingSwitch = slotMap.get(switchKey);
        if (!existingSwitch) {
          slotMap.set(switchKey, {
            label: switchSlot.label,
            sortKey: switchSlot.sortKey,
            occupied: participants,
            type: 'resourceSwitch',
            resourceSwitch: {
              fromResourceName: getSegmentResourceName(previousDistinctSegment.seg, orderedResourcesConfig),
              toResourceName: getSegmentResourceName(segmentEntry.seg, orderedResourcesConfig),
            },
          });
        } else {
          existingSwitch.occupied += participants;
        }
      }

      previousDistinctSegment = segmentEntry;
    }
  }

  return annotateSlotEntries(Array.from(slotMap.values()));
};

function VertGolf({
  bookings,
  date,
  slotCapacity,
  updateBooking,
  capacity,
  capacityTimes,
  handleOpenModal,
  orderedResourcesConfig,
}) {
  const { preferences } = useAuth();
  const durationByIdMs = orderedResourcesConfig?.durationByIdMs || null;
  const openModalNoBorder = bookingId => handleOpenModal?.(bookingId, null);
  const isIntervalMode = Boolean(orderedResourcesConfig?.isIntervalMode);
  const showResourceSwitchCards = preferences?.golfVerticalShowResourceSwitchCards !== false;
  const showStaggeredGroupCards = preferences?.golfVerticalShowStaggeredGroupCards !== false;
  const resolveBookingSlotOccupancyForGolf = useCallback(
    (booking, startDate, hasValidDate, timeFormatter) => {
      if (!isIntervalMode) return undefined;
      return resolveGolfSlotOccupancy(
        booking,
        startDate,
        hasValidDate,
        timeFormatter,
        orderedResourcesConfig,
        { includeResourceSwitchEntries: showResourceSwitchCards }
      );
    },
    [isIntervalMode, orderedResourcesConfig, showResourceSwitchCards]
  );

  const shouldRenderGolfCardForSlotEntry = useCallback(
    ({ entry, isFirstEntry }) => {
      if (entry?.type === 'resourceSwitch') {
        return showResourceSwitchCards;
      }
      return showStaggeredGroupCards || isFirstEntry;
    },
    [showResourceSwitchCards, showStaggeredGroupCards]
  );

  const renderBookingCard = (booking, updateBooking, wave) => {
    if (booking.bookingType === 'InternalBooking') {
      return (
        <BookingCardGolfv2
          key={booking._id}
          booking={booking}
          handleOpenModal={openModalNoBorder}
          durationByIdMs={durationByIdMs}
          orderedResourcesConfig={orderedResourcesConfig}
          wave={wave}
        />
      );
    }
    return (
      <BookingCardGolf key={booking._id} booking={booking} updateBookingState={updateBooking} />
    );
  };

  return (
    <VerticalViewBase
      bookings={bookings}
      date={date}
      slotCapacity={slotCapacity}
      updateBooking={updateBooking}
      renderBookingCard={renderBookingCard}
      showCapacity={true}
      showPercentage={true}
      capacityData={capacity}
      capacityTimes={capacityTimes}
      resolveBookingSlotOccupancy={isIntervalMode ? resolveBookingSlotOccupancyForGolf : undefined}
      renderCardPerSlot={isIntervalMode && showStaggeredGroupCards}
      shouldRenderCardForSlotEntry={
        isIntervalMode ? shouldRenderGolfCardForSlotEntry : undefined
      }
    />
  );
}

export default VertGolf;
